import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { api } from "@/lib/api";
import PageRenderer from "@/components/PageRenderer";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Mail, Phone, MapPin, Heart, ShieldCheck, ScrollText, Flame, Sparkles, Facebook, Instagram, Twitter, Youtube, Linkedin, Star, MessageSquare } from "lucide-react";

const HERO = "https://images.unsplash.com/photo-1601821139314-66a4d14cfc00?crop=entropy&cs=srgb&fm=jpg&q=85";
const GALLERY = [
  "https://images.unsplash.com/photo-1605302977545-3a09913be1dd?crop=entropy&cs=srgb&fm=jpg&q=85",
  "https://images.unsplash.com/photo-1630764883473-e8c2056f0589?crop=entropy&cs=srgb&fm=jpg&q=85",
  "https://images.unsplash.com/photo-1665003725647-3ae0f01140b1?crop=entropy&cs=srgb&fm=jpg&q=85",
  "https://images.unsplash.com/photo-1699764681875-dd04ce36b1c3?crop=entropy&cs=srgb&fm=jpg&q=85",
];
const STEPS = [
  { title: "Choose", text: "Pick a Mandir or a worship/Vedic ritual and the date & time that suits you." },
  { title: "Select Purohit", text: "See verified Purohits available at that Mandir and choose the one you trust." },
  { title: "Add devotees & plan", text: "Add everyone attending, pick a plan and any value-added offerings." },
  { title: "Confirm & receive", text: "Pay securely, get instant confirmation and blessed prasad after the puja." },
];
const BELIEFS = [
  { icon: ShieldCheck, title: "Trust", note: "Every Purohit is manually verified before onboarding." },
  { icon: Sparkles, title: "Authenticity", note: "Complete vidhi performed with genuine samagri." },
  { icon: Flame, title: "Tradition", note: "Rooted in the timeless practices of the Vedas." },
  { icon: Heart, title: "Devotion", note: "Every ritual carried out with sincerity and care." },
];
const SOCIALS = [
  { icon: Facebook, label: "Facebook" }, { icon: Instagram, label: "Instagram" },
  { icon: Twitter, label: "Twitter" }, { icon: Youtube, label: "YouTube" }, { icon: Linkedin, label: "LinkedIn" },
];
const FEEDBACK_Q = [
  "How would you rate your Purohit experience?",
  "How satisfied were you with the Puja-Samagri Kit?",
  "How satisfied were you with the Worship / Vedic Ritual experience?",
  "Your experience & review (within 100 words)",
];

function Gallery() {
  const [i, setI] = useState(0);
  useEffect(() => { const t = setInterval(() => setI((p) => (p + 1) % GALLERY.length), 3500); return () => clearInterval(t); }, []);
  return (
    <div className="relative rounded-2xl overflow-hidden aspect-[4/3] border border-border" data-testid="about-gallery">
      <AnimatePresence mode="wait">
        <motion.img key={i} src={GALLERY[i]} alt="" initial={{ opacity: 0, scale: 1.05 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.9 }} className="absolute inset-0 h-full w-full object-cover" />
      </AnimatePresence>
      <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-1.5">
        {GALLERY.map((_, idx) => <button key={idx} onClick={() => setI(idx)} className={`h-1.5 rounded-full transition-all ${idx === i ? "w-5 bg-white" : "w-1.5 bg-white/50"}`} />)}
      </div>
    </div>
  );
}

export default function About() {
  const [cmsPage, setCmsPage] = useState(undefined);
  useEffect(() => { api.get("/pages/about").then((r) => setCmsPage(r.data)).catch(() => setCmsPage(null)); }, []);
  const [faqs, setFaqs] = useState([]);
  useEffect(() => { api.get("/faqs").then((r) => setFaqs(r.data)); }, []);
  if (cmsPage) return <PageRenderer page={cmsPage} />;

  return (
    <div data-testid="about-page">
      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0"><img src={HERO} alt="" className="h-full w-full object-cover" /><div className="absolute inset-0 bg-gradient-to-r from-background via-background/92 to-background/55" /></div>
        <div className="relative px-4 sm:px-6 lg:px-12 max-w-7xl mx-auto py-16 lg:py-24">
          <p className="font-devanagari text-accent text-lg mb-2">॥ परिचय ॥</p>
          <h1 className="text-4xl sm:text-5xl font-serif font-bold max-w-2xl leading-tight">About Pavitra Mantra</h1>
          <p className="mt-4 text-muted-foreground max-w-xl text-lg">Bridging devotees, sacred Mandirs and verified Purohits through technology, tradition and trust.</p>
          <div className="flex flex-wrap gap-2 mt-6">
            {[["about", "About Us"], ["contact", "Contact & Support"], ["faq", "FAQ"], ["feedback", "Feedback"]].map(([h, l]) => (
              <a key={h} href={`#${h}`} data-testid={`about-nav-${h}`} className="text-sm px-4 py-2 rounded-full border border-border bg-card/70 backdrop-blur hover:border-accent transition-colors">{l}</a>
            ))}
          </div>
        </div>
      </section>

      {/* 1. About Us */}
      <section id="about" className="px-4 sm:px-6 lg:px-12 max-w-7xl mx-auto py-16 scroll-mt-20" data-testid="about-section">
        <div className="grid lg:grid-cols-2 gap-10 items-center">
          <div>
            <p className="text-xs uppercase tracking-[0.25em] text-accent font-semibold mb-2">Faith simplified</p>
            <h2 className="font-serif text-3xl font-bold leading-tight">Why we exist — <span className="text-primary">Connecting devotees with worship</span></h2>
            <p className="text-muted-foreground leading-relaxed mt-4">Distance, time and unfamiliar rituals should never stand between a devotee and their faith. Our mission is to make discovering sacred Mandirs and arranging authentic worship effortless — so devotion, not logistics, stays at the centre.</p>
          </div>
          <Gallery />
        </div>

        <div className="mt-14 rounded-2xl border border-border bg-secondary/30 p-7">
          <h3 className="font-serif text-2xl font-bold">Our Purpose — <span className="text-primary">Making traditional worship more simplified</span></h3>
          <p className="text-muted-foreground leading-relaxed mt-3">Pavitra Mantra was created to make discovering and arranging these sacred experiences simpler, more convenient and accessible—while respecting the traditions behind them. We bring devotees, Mandirs and qualified Pandits together through one digital platform.</p>
        </div>

        <div className="mt-14">
          <h3 className="font-serif text-2xl font-bold mb-6">How it works</h3>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="about-how-steps">
            {STEPS.map((s, i) => (
              <motion.div key={s.title} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.08 }} className="rounded-2xl border border-border bg-card p-5">
                <span className="h-9 w-9 grid place-items-center rounded-full bg-primary text-primary-foreground font-serif font-bold mb-3">{i + 1}</span>
                <h4 className="font-serif text-lg font-semibold">{s.title}</h4>
                <p className="text-sm text-muted-foreground mt-1.5 leading-relaxed">{s.text}</p>
              </motion.div>
            ))}
          </div>
        </div>

        <div className="mt-14">
          <h3 className="font-serif text-2xl font-bold">What we Believe — <span className="text-primary">Rooted in Tradition. Designed for Today.</span></h3>
          <p className="text-muted-foreground leading-relaxed mt-3 max-w-3xl">We honour the sanctity of every ritual while using technology to remove friction. Ancient wisdom guides the worship; thoughtful design makes it reachable for every devotee, wherever they are.</p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-6" data-testid="about-beliefs">
            {BELIEFS.map((b) => (
              <div key={b.title} className="rounded-2xl border border-border bg-card p-5 flex items-start gap-4">
                <span className="h-11 w-11 shrink-0 grid place-items-center rounded-xl bg-accent/15 text-accent"><b.icon className="h-5 w-5" /></span>
                <div><p className="font-serif font-semibold">{b.title}</p><p className="text-sm text-muted-foreground mt-0.5 leading-relaxed">{b.note}</p></div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 2. Contact & Support */}
      <section id="contact" className="bg-secondary/40 border-y border-border scroll-mt-20" data-testid="contact-section">
        <div className="px-4 sm:px-6 lg:px-12 max-w-7xl mx-auto py-16 grid lg:grid-cols-2 gap-10">
          <div>
            <p className="text-xs uppercase tracking-[0.25em] text-accent font-semibold mb-2">Reach us</p>
            <h2 className="font-serif text-3xl font-bold">Contact &amp; Support</h2>
            <p className="text-muted-foreground mt-3 mb-6">Questions or need help with a booking? Our team is here to help.</p>
            <div className="rounded-2xl border border-border bg-card p-6">
              <p className="font-serif text-lg font-semibold">Pavitra Mantra Private Limited</p>
              <p className="text-sm text-muted-foreground mt-1 leading-relaxed">11, Shiv Ganga Road, Baba Dham<br />B. Deoghar (Jharkhand) - 814112</p>
            </div>
          </div>
          <div className="space-y-4">
            <a href="mailto:pm.pavitramantra@gmail.com" data-testid="contact-email-link" className="flex items-center gap-3 rounded-2xl border border-border bg-card p-4 hover:border-accent transition-colors">
              <span className="h-11 w-11 grid place-items-center rounded-xl bg-accent/15 text-accent"><Mail className="h-5 w-5" /></span>
              <div><p className="text-xs text-muted-foreground">Email us</p><p className="font-medium">pm.pavitramantra@gmail.com</p></div>
            </a>
            <a href="tel:+916294986115" data-testid="contact-phone-link" className="flex items-center gap-3 rounded-2xl border border-border bg-card p-4 hover:border-accent transition-colors">
              <span className="h-11 w-11 grid place-items-center rounded-xl bg-accent/15 text-accent"><Phone className="h-5 w-5" /></span>
              <div><p className="text-xs text-muted-foreground">Chat with us</p><p className="font-medium">+91-6294986115</p></div>
            </a>
            <div className="rounded-2xl border border-border bg-card p-4">
              <p className="text-xs text-muted-foreground mb-3">Follow us on social media</p>
              <div className="flex gap-3">
                {SOCIALS.map((s) => (
                  <a key={s.label} href="#" aria-label={s.label} data-testid={`social-${s.label.toLowerCase()}`} className="h-11 w-11 grid place-items-center rounded-xl border border-border text-foreground hover:bg-accent hover:text-accent-foreground hover:border-accent transition-colors"><s.icon className="h-5 w-5" /></a>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 3. FAQ */}
      <section id="faq" className="scroll-mt-20" data-testid="faq-section">
        <div className="px-4 sm:px-6 lg:px-12 max-w-3xl mx-auto py-16">
          <p className="text-xs uppercase tracking-[0.25em] text-accent font-semibold mb-2 text-center">Help</p>
          <h2 className="font-serif text-3xl font-bold mb-8 text-center">Frequently Asked Questions</h2>
          <Accordion type="single" collapsible className="space-y-3" data-testid="faq-accordion">
            {faqs.map((f, i) => (
              <AccordionItem key={f.id || i} value={`faq-${i}`} data-testid={`faq-item-${i}`} className="rounded-xl border border-border bg-card px-5">
                <AccordionTrigger className="text-left font-medium" data-testid={`faq-${i}`}>{f.q}</AccordionTrigger>
                <AccordionContent className="text-muted-foreground">{f.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* 4. Feedback */}
      <section id="feedback" className="bg-secondary/40 border-t border-border scroll-mt-20" data-testid="feedback-section">
        <div className="px-4 sm:px-6 lg:px-12 max-w-3xl mx-auto py-16">
          <p className="text-xs uppercase tracking-[0.25em] text-accent font-semibold mb-2 text-center">Your voice matters</p>
          <h2 className="font-serif text-3xl font-bold mb-3 text-center">Feedback &amp; Review</h2>
          <p className="text-muted-foreground text-center max-w-2xl mx-auto">After your worship or Vedic ritual is completed, we automatically email you a short feedback request. Your ratings and review are linked to the Mandir's Purohit profile to help fellow devotees choose with confidence.</p>
          <div className="rounded-2xl border border-border bg-card p-7 mt-8" data-testid="feedback-preview">
            <p className="text-sm text-muted-foreground mb-5">What we'll ask you:</p>
            <div className="space-y-4">
              {FEEDBACK_Q.map((q, i) => (
                <div key={i} className="border-b border-border last:border-0 pb-4 last:pb-0">
                  <p className="font-medium mb-2">Q{i + 1}. {q}</p>
                  {i < 3 ? (
                    <div className="flex gap-1.5 text-muted-foreground/40">{[1, 2, 3, 4, 5].map((n) => <Star key={n} className="h-6 w-6" />)}</div>
                  ) : (
                    <div className="rounded-xl border border-dashed border-border p-3 text-sm text-muted-foreground/60 flex items-center gap-2"><MessageSquare className="h-4 w-4" /> Write your review (max 100 words)</div>
                  )}
                </div>
              ))}
            </div>
            <p className="text-xs text-muted-foreground mt-5">You'll be able to submit this from <span className="font-medium text-foreground">My Bookings</span> once your booking is marked completed.</p>
          </div>
        </div>
      </section>
    </div>
  );
}
